import { useEffect, useState } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

import { api } from '@/services/api.service'
import ROUTES from '@/ROUTES'
import AdminPageHeader from '@/components/AdminPageHeader'
import Table from '@/components/Table'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler)

// ---- Types ----

interface SubscriptionRow {
    _id: string
    name: string
    email: string
    subscriptionStatus: string
    planType?: string
    stripeSubscriptionId: string
    benefitsEndDate?: string
    trialEndDate?: string
    createdAt: string
}

interface DropoffRow {
    name: string
    email: string
    createdAt: string
    lastLoginAt?: string
}

interface EngagementRow {
    name: string
    email: string
    createdAt: string
    lastLoginAt?: string
    onboardingComplete: boolean
    chatCount: number
    completedMilestones: number
    completedTasks: number
    totalMilestones: number
}

interface ChurnRow {
    name: string
    email: string
    planType?: string
    createdAt: string
    updatedAt: string
    daysActive: number
    trialEndDate?: string
}

interface RegistrationRow {
    name: string
    email: string
    createdAt: string
    onboardingComplete?: boolean
    lastLoginAt?: string
    subscriptionStatus?: string
    planType?: string
}

interface AnalyticsData {
    summary: {
        totalUsers: number
        registrationsToday: number
        loginsToday: number
        chatsToday: number
        totalSubscriptions: number
        activeSubscriptions: number
    }
    registrationsByDay: { _id: string; count: number }[]
    loginsByDay: { _id: string; count: number }[]
    chatMessagesByDay: { _id: string; count: number }[]
    recentLogins: { timestamp: string; name: string; email: string }[]
    subscriptionsByStatus: { _id: string; count: number }[]
    subscriptions: SubscriptionRow[]
    conversionFunnel?: {
        registered: number
        onboarded: number
        shownPaywall: number
        subscribed: number
    }
    activation?: {
        totalUsers: number
        onboardingComplete: number
        completedFirstTask: number
        hadFirstChat: number
    }
    retention?: {
        totalSubscriptions: number
        activeSubscriptions: number
        totalCanceled: number
        trialCount: number
        trialsCanceled: number
        canceledBeforeFirstRenewal: number
        planBreakdown: { _id: string; count: number }[]
    }
    onboardingDropoffs?: DropoffRow[]
    engagementLeaderboard?: EngagementRow[]
    churnDetail?: ChurnRow[]
    recentRegistrations?: RegistrationRow[]
    paywallFeedback?: {
        totalDismissals: number
        uniqueUsers: number
        reasonBreakdown: { _id: string; count: number }[]
        recentFeedback: PaywallFeedbackRow[]
        thresholdTriggers: { milestones: number; chats: number; both: number }
    }
}

interface PaywallFeedbackRow {
    name: string
    email: string
    reason: string
    otherText?: string
    createdAt: string
}

type SectionId = 'registrations' | 'dropoffs' | 'engagement' | 'churn' | 'subscriptions' | 'logins' | 'paywallFeedback'

// GA4 deep links
const GA4_BASE = 'https://analytics.google.com/analytics/web/#/a368260535p504884011'
const GA4_LINKS: { question: string; description: string; url: string }[] = [
    {
        question: 'Where are users coming from?',
        description: 'Traffic sources, campaigns, referrals',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22%5D&r=lifecycle-traffic-acquisition-v2`,
    },
    {
        question: 'How is the email campaign performing?',
        description: 'Newsletter traffic (utm_source=newsletter, utm_medium=email)',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22%5D%26_r.explorerCard..seldim%3D%5B%22sessionPrimaryChannelGroup%22,%22hostname%22%5D%26_r..dimension-value%3D%7B%22dimension%22:%22sessionMedium%22,%22value%22:%22email%22%7D&r=lifecycle-traffic-acquisition-v2`,
    },
    {
        question: 'How is the social media campaign performing?',
        description: 'April 2026 social push — Facebook vs Instagram vs X vs Pinterest',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22,%22sessions%22,%22engagementRate%22%5D%26_r.explorerCard..seldim%3D%5B%22sessionMedium%22,%22hostname%22%5D%26_r..dataFilters%3D%5B%7B%22type%22:1,%22fieldName%22:%22sessionCampaignName%22,%22evaluationType%22:1,%22expressionList%22:%5B%22april-2026-social%22%5D,%22complement%22:false,%22isCaseSensitive%22:true,%22expression%22:%22%22%7D%5D&r=lifecycle-traffic-acquisition-v2`,
    },
    {
        question: 'All campaigns viewed together',
        description: 'All named campaigns excluding direct/referral/organic, 50 rows',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..selmet%3D%5B%22activeUsers%22,%22sessions%22,%22engagementRate%22%5D%26_r.explorerCard..seldim%3D%5B%22sessionCampaignName%22,%22sessionSourceMedium%22%5D%26_r.explorerCard..rowsPerPage%3D50%26_r.explorerCard..startRow%3D0%26_r..dataFilters%3D%5B%7B%22type%22:1,%22fieldName%22:%22sessionCampaignName%22,%22evaluationType%22:8,%22expressionList%22:%5B%22(direct)%22,%22(referral)%22,%22(organic)%22%5D,%22complement%22:true,%22isCaseSensitive%22:true,%22expression%22:%22%22%7D%5D%26_r.copa-filter-builder..filter-to-edit%3D%5B%7B%22type%22:1,%22fieldName%22:%22sessionCampaignName%22,%22evaluationType%22:8,%22expressionList%22:%5B%22(direct)%22,%22(referral)%22,%22(organic)%22%5D,%22complement%22:true,%22isCaseSensitive%22:true,%22expression%22:%22%22%7D%5D&r=lifecycle-traffic-acquisition-v2`,
    },
    {
        question: 'Who visited registration page, but didn\'t necessarily sign up?',
        description: 'Compare this data against actual registration counts shown in below sections',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui%26_r.explorerCard..seldim%3D%5B%22landingPageMinusQueryString%22,%22sessionSourceMedium%22%5D%26_r..dimension-value%3D%7B%22dimension%22:%22landingPageMinusQueryString%22,%22value%22:%22%2Fregister%22%7D&ruid=E7E2B7A6-BB33-4E6F-A475-BCF08C84F696&collectionId=12259570465&r=landing-page`,
    },
    {
        question: 'What features are users interacting with?',
        description: 'All custom events: chat, roadmap, resources, onboarding',
        url: `${GA4_BASE}/reports/explorer?params=_u..nav%3Dmaui&r=top-events&ruid=454530BF-36BA-4C23-A2AF-E36AE396D2BB&collectionId=12259570465`,
    },
    {
        question: 'Are users completing tasks?',
        description: 'task_completed event detail',
        url: `${GA4_BASE}/reports/dashboard?params=_u..nav%3Dmaui%26_r..dimension-value%3D%7B%22dimension%22:%22eventName%22,%22value%22:%22task_completed%22%7D&r=events-overview&collectionId=12259570465`,
    },
    {
        question: 'Are users chatting with the AI?',
        description: 'message_sent event detail',
        url: `${GA4_BASE}/reports/dashboard?params=_u..nav%3Dmaui%26_r..dimension-value%3D%7B%22dimension%22:%22eventName%22,%22value%22:%22message_sent%22%7D&r=events-overview&collectionId=12259570465`,
    },
    {
        question: 'Which pages get the most views?',
        description: 'Pages and screens breakdown',
        url: `${GA4_BASE}/reports/explorer?params=_r.explorerCard..seldim%3D%5B%22unifiedScreenClass%22%5D%26_u..nav%3Dmaui&r=all-pages-and-screens&ruid=44F082EE-F9A6-42BA-A56A-D76A3737F7DD&collectionId=12259570465`,
    },
    {
        question: "What's happening today?",
        description: "Today's user activity, new users, engagement",
        url: `${GA4_BASE}/reports/dashboard?params=_u..nav%3Dmaui%26_u.comparisonOption%3Ddisabled%26_r.3..selmet%3D%5B%22newUsers%22%5D%26_u.dateOption%3Dtoday&collectionId=12259570465&ruid=98AB0258-E729-4A9F-92BE-1B4C75AA1414&r=business-objectives-examine-user-behavior-overview`,
    },
    {
        question: 'What does real-time activity look like?',
        description: 'Live users, active pages, events firing now',
        url: `${GA4_BASE}/realtime/overview?params=_u..nav%3Dmaui&collectionId=12259570465`,
    },
    {
        question: 'Where are users located (by state)?',
        description: 'Geographic heat map of users by US state/region',
        url: 'https://analytics.google.com/analytics/web/#/analysis/a368260535p504884011/edit/Li6kzf1LQnqadU18WhN2UA?restoreUserState=true',
    },
    {
        question: 'Which cities are users in?',
        description: 'Geographic heat map of users by US city',
        url: 'https://analytics.google.com/analytics/web/#/analysis/a368260535p504884011/edit/rzEBlAa_TtuWoyQOiMECGg?restoreUserState=true',
    },
]

const FEEDBACK_REASON_LABELS: Record<string, string> = {
    still_exploring: 'Still exploring',
    price_high: 'Price too high',
    not_right: 'Not right for me',
    other: 'Other',
    // Legacy reason codes (backward compatibility)
    useful_free: 'Useful but don\'t want to pay',
    too_expensive: 'Too expensive',
    not_ready: 'Not ready to decide',
}

// ---- Helpers ----

function fillMissingDays(data: { _id: string; count: number }[] | undefined, days: number): { date: string; count: number }[] {
    if (!data) return []
    const map = new Map(data.map(d => [d._id, d.count]))
    const result: { date: string; count: number }[] = []
    const now = new Date()
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const key = d.toISOString().slice(0, 10)
        result.push({ date: key, count: map.get(key) ?? 0 })
    }
    return result
}

function formatDate(d: string | undefined) {
    return d ? format(new Date(d), 'MMM d, yyyy') : '\u2014'
}

function formatAgo(d: string | undefined) {
    return d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '\u2014'
}

function StatusBadge({ status }: { status: string | null | undefined }) {
    if (!status) return <span className='text-gray-400'>\u2014</span>
    const colors: Record<string, string> = {
        active: 'bg-green-100 text-green-800',
        trialing: 'bg-blue-100 text-blue-800',
        canceled: 'bg-red-100 text-red-800',
        past_due: 'bg-yellow-100 text-yellow-800',
        paused: 'bg-gray-100 text-gray-800',
    }
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    )
}

function BoolBadge({ value }: { value?: boolean }) {
    return value
        ? <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>Yes</span>
        : <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>No</span>
}

// ---- Card Components ----

function StatCard({ label, value, onClick }: { label: string; value: number; onClick?: () => void }) {
    return (
        <div
            className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm${onClick ? ' hover:border-brand-300 hover:shadow-md transition-all cursor-pointer' : ''}`}
            onClick={onClick}
        >
            <div className='flex items-center justify-between'>
                <p className='text-sm font-medium text-gray-500'>{label}</p>
                {onClick && <ChevronRightIcon className='h-4 w-4 text-gray-400' />}
            </div>
            <p className='mt-1 text-3xl font-bold text-gray-900'>{value.toLocaleString()}</p>
        </div>
    )
}

function ChartCard({ title, data, color }: { title: string; data: { date: string; count: number }[]; color: string }) {
    const chartData = {
        labels: data.map(d => format(new Date(d.date + 'T00:00:00'), 'MMM d')),
        datasets: [{
            label: title,
            data: data.map(d => d.count),
            borderColor: color,
            backgroundColor: color + '20',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
        }],
    }
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1 } },
            x: { ticks: { maxTicksLimit: 10 } },
        },
    }
    return (
        <div className='bg-white rounded-xl border border-gray-200 p-5 shadow-sm'>
            <h3 className='text-sm font-semibold text-gray-700 mb-3'>{title}</h3>
            <div className='h-48'>
                <Line data={chartData} options={options} />
            </div>
        </div>
    )
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <div className='px-4 sm:px-6 lg:px-8 pt-4'>
            <h2 className='text-lg font-semibold text-gray-800'>{title}</h2>
            <p className='text-sm text-gray-500'>{subtitle}</p>
        </div>
    )
}

function FunnelBar({ label, count, maxCount, prevCount, color, onClick }: {
    label: string
    count: number
    maxCount: number
    prevCount?: number
    color: string
    onClick?: () => void
}) {
    const width = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 4 : 0) : 0
    const rate = prevCount !== undefined && prevCount > 0 ? (count / prevCount) * 100 : undefined
    return (
        <div className={`space-y-1${onClick ? ' cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-1 rounded-lg transition-colors' : ''}`} onClick={onClick}>
            <div className='flex items-center justify-between'>
                <span className='text-sm font-medium text-gray-700'>{label}</span>
                <div className='flex items-center gap-2'>
                    {rate !== undefined && (
                        <span className='text-xs font-medium text-gray-400'>
                            {rate.toFixed(0)}% conversion
                        </span>
                    )}
                    <span className='text-sm font-bold text-gray-900'>{count.toLocaleString()}</span>
                    {onClick && <ChevronRightIcon className='h-3.5 w-3.5 text-gray-400' />}
                </div>
            </div>
            <div className='h-8 bg-gray-100 rounded-lg overflow-hidden'>
                <div
                    className='h-full rounded-lg transition-all duration-500'
                    style={{ width: `${width}%`, backgroundColor: color }}
                />
            </div>
        </div>
    )
}

function ActivationCard({ label, count, total, color, onClick }: {
    label: string
    count: number
    total: number
    color: string
    onClick?: () => void
}) {
    const pct = total > 0 ? (count / total) * 100 : 0
    return (
        <div
            className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm${onClick ? ' hover:border-brand-300 hover:shadow-md transition-all cursor-pointer' : ''}`}
            onClick={onClick}
        >
            <div className='flex items-center justify-between mb-3'>
                <p className='text-sm font-medium text-gray-500'>{label}</p>
                <div className='flex items-center gap-2'>
                    <p className='text-2xl font-bold text-gray-900'>{pct.toFixed(0)}%</p>
                    {onClick && <ChevronRightIcon className='h-4 w-4 text-gray-400' />}
                </div>
            </div>
            <div className='h-2 bg-gray-100 rounded-full overflow-hidden'>
                <div
                    className='h-full rounded-full transition-all duration-500'
                    style={{ width: `${pct}%`, backgroundColor: color }}
                />
            </div>
            <p className='mt-2 text-xs text-gray-400'>{count.toLocaleString()} of {total.toLocaleString()} users</p>
        </div>
    )
}

function MetricCard({ label, value, subtitle, onClick }: { label: string; value: string; subtitle?: string; onClick?: () => void }) {
    return (
        <div
            className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm${onClick ? ' hover:border-brand-300 hover:shadow-md transition-all cursor-pointer' : ''}`}
            onClick={onClick}
        >
            <div className='flex items-center justify-between'>
                <p className='text-sm font-medium text-gray-500'>{label}</p>
                {onClick && <ChevronRightIcon className='h-4 w-4 text-gray-400' />}
            </div>
            <p className='mt-1 text-3xl font-bold text-gray-900'>{value}</p>
            {subtitle && <p className='mt-1 text-xs text-gray-400'>{subtitle}</p>}
        </div>
    )
}

function CollapsibleSection({ id, title, count, isOpen, onToggle, children }: {
    id: string
    title: string
    count: number
    isOpen: boolean
    onToggle: () => void
    children: React.ReactNode
}) {
    return (
        <div id={`section-${id}`} className='px-4 sm:px-6 lg:px-8'>
            <button
                onClick={onToggle}
                className='flex items-center gap-2 text-lg font-semibold text-gray-800 mb-3 hover:text-brand-600 transition-colors'
            >
                {isOpen ? <ChevronDownIcon className='h-5 w-5' /> : <ChevronRightIcon className='h-5 w-5' />}
                {title}
                <span className='text-sm font-normal text-gray-400'>({count})</span>
            </button>
            {isOpen && children}
        </div>
    )
}

// ---- Main Component ----

export default function AdminAnalytics() {
    const [data, setData] = useState<AnalyticsData | null>(null)
    const [days, setDays] = useState(30)
    const [loading, setLoading] = useState(true)
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

    useEffect(() => {
        setLoading(true)
        api<AnalyticsData>(`${ROUTES.admin.analytics}?days=${days}`)
            .then(res => {
                if (res.data) setData(res.data)
            })
            .finally(() => setLoading(false))
    }, [days])

    const registrations = data ? fillMissingDays(data.registrationsByDay, days) : []
    const logins = data ? fillMissingDays(data.loginsByDay, days) : []
    const chats = data ? fillMissingDays(data.chatMessagesByDay, days) : []
    const funnel = data?.conversionFunnel
    const activation = data?.activation
    const retention = data?.retention

    const toggleSection = (id: string) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }))

    const scrollToSection = (id: SectionId) => {
        setOpenSections(prev => ({ ...prev, [id]: true }))
        setTimeout(() => {
            document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
    }

    return (
        <div className='flex flex-col w-full max-w-7xl gap-5 py-10 mx-auto'>
            <AdminPageHeader
                heading='Analytics'
                subheading='Usage metrics and activity trends'
                action={
                    <select
                        value={days}
                        onChange={e => setDays(Number(e.target.value))}
                        className='rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500'
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                    </select>
                }
            />

            {loading && !data ? (
                <div className='flex items-center justify-center py-20'>
                    <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-500' />
                </div>
            ) : data ? (
                <>
                    {/* Summary Cards */}
                    <div className='grid grid-cols-2 lg:grid-cols-3 gap-4 px-4 sm:px-6 lg:px-8'>
                        <StatCard label='Total Users' value={data.summary.totalUsers} onClick={() => scrollToSection('engagement')} />
                        <StatCard label='Registrations Today' value={data.summary.registrationsToday} onClick={() => scrollToSection('registrations')} />
                        <StatCard label='Logins Today' value={data.summary.loginsToday} onClick={() => scrollToSection('logins')} />
                        <StatCard label='Chat Messages Today' value={data.summary.chatsToday} onClick={() => scrollToSection('engagement')} />
                        <StatCard label='Active Subscriptions' value={data.summary.activeSubscriptions} onClick={() => scrollToSection('subscriptions')} />
                        <StatCard label='Total Subscriptions' value={data.summary.totalSubscriptions} onClick={() => scrollToSection('subscriptions')} />
                    </div>

                    {/* GA4 Deep Links */}
                    <SectionHeader title='Google Analytics' subtitle='Jump to key reports in GA4' />
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-4 sm:px-6 lg:px-8'>
                        {GA4_LINKS.map((link) => (
                            <a
                                key={link.question}
                                href={link.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:border-blue-300 hover:shadow-md transition-all group'
                            >
                                <p className='text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors'>
                                    {link.question}
                                </p>
                                <p className='mt-1 text-xs text-gray-400'>{link.description}</p>
                            </a>
                        ))}
                    </div>

                    {/* Conversion Funnel */}
                    {funnel && (
                        <>
                            <SectionHeader
                                title='Conversion Funnel'
                                subtitle={`Cohort: users who registered in the last ${days} days`}
                            />
                            <div className='px-4 sm:px-6 lg:px-8'>
                                <div className='bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-3'>
                                    {funnel.registered > 0 ? (
                                        <>
                                            <FunnelBar label='Registered' count={funnel.registered} maxCount={funnel.registered} color='#6366f1' onClick={() => scrollToSection('registrations')} />
                                            <FunnelBar label='Completed Onboarding' count={funnel.onboarded} maxCount={funnel.registered} prevCount={funnel.registered} color='#8b5cf6' onClick={() => scrollToSection('registrations')} />
                                            <FunnelBar label='Shown Paywall' count={funnel.shownPaywall ?? 0} maxCount={funnel.registered} prevCount={funnel.onboarded} color='#a855f7' onClick={() => scrollToSection('paywallFeedback')} />
                                            <FunnelBar label='Subscribed / Paying' count={funnel.subscribed} maxCount={funnel.registered} prevCount={funnel.shownPaywall ?? 0} color='#10b981' onClick={() => scrollToSection('subscriptions')} />
                                        </>
                                    ) : (
                                        <p className='text-sm text-gray-400 text-center py-4'>No registrations in this period</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Activation */}
                    {activation && (
                        <>
                            <SectionHeader title='Activation' subtitle='All-time metrics across all users' />
                            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 sm:px-6 lg:px-8'>
                                <ActivationCard label='Completed Onboarding' count={activation.onboardingComplete} total={activation.totalUsers} color='#10b981' onClick={() => scrollToSection('dropoffs')} />
                                <ActivationCard label='Completed First Task' count={activation.completedFirstTask} total={activation.totalUsers} color='#3b82f6' onClick={() => scrollToSection('engagement')} />
                                <ActivationCard label='Had First AI Chat' count={activation.hadFirstChat} total={activation.totalUsers} color='#8b5cf6' onClick={() => scrollToSection('engagement')} />
                            </div>
                        </>
                    )}

                    {/* Engagement Charts */}
                    <SectionHeader title='Engagement Trends' subtitle={`Activity over the last ${days} days`} />
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 sm:px-6 lg:px-8'>
                        <ChartCard title='Registrations' data={registrations} color='#10b981' />
                        <ChartCard title='Unique Logins' data={logins} color='#3b82f6' />
                        <ChartCard title='Chat Messages' data={chats} color='#8b5cf6' />
                    </div>

                    {/* Retention & Churn */}
                    {retention && (
                        <>
                            <SectionHeader title='Retention & Churn' subtitle='Subscription lifecycle metrics' />
                            <div className='grid grid-cols-2 lg:grid-cols-5 gap-4 px-4 sm:px-6 lg:px-8'>
                                <MetricCard
                                    label='Trial Cancel Rate'
                                    value={retention.trialCount > 0 ? `${((retention.trialsCanceled / retention.trialCount) * 100).toFixed(0)}%` : '\u2014'}
                                    subtitle={`${retention.trialsCanceled} of ${retention.trialCount} trials`}
                                    onClick={() => scrollToSection('churn')}
                                />
                                <MetricCard
                                    label='Early Churn'
                                    value={retention.totalSubscriptions > 0 ? `${((retention.canceledBeforeFirstRenewal / retention.totalSubscriptions) * 100).toFixed(0)}%` : '\u2014'}
                                    subtitle='Canceled within 35 days'
                                    onClick={() => scrollToSection('churn')}
                                />
                                <MetricCard
                                    label='Active'
                                    value={String(retention.activeSubscriptions)}
                                    subtitle='Active + trialing'
                                    onClick={() => scrollToSection('subscriptions')}
                                />
                                <MetricCard
                                    label='Canceled'
                                    value={String(retention.totalCanceled)}
                                    subtitle='All-time'
                                    onClick={() => scrollToSection('churn')}
                                />
                                <MetricCard
                                    label='Plan Split'
                                    value={retention.planBreakdown.length > 0
                                        ? retention.planBreakdown.map(p => `${p.count} ${p._id}`).join(', ')
                                        : '\u2014'}
                                    subtitle='Active subscriptions by plan'
                                    onClick={() => scrollToSection('subscriptions')}
                                />
                            </div>
                        </>
                    )}

                    {/* Paywall Interactions */}
                    {data.paywallFeedback && (
                        <>
                            <SectionHeader title='Paywall Interactions' subtitle='How users respond when they hit the paywall' />
                            <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 px-4 sm:px-6 lg:px-8'>
                                <MetricCard
                                    label='"Maybe Later" Clicks'
                                    value={String(data.paywallFeedback.totalDismissals)}
                                    subtitle={`${data.paywallFeedback.uniqueUsers} unique users`}
                                    onClick={() => scrollToSection('paywallFeedback')}
                                />
                                <MetricCard
                                    label='Subscribed'
                                    value={String(data.summary.totalSubscriptions)}
                                    subtitle='Clicked through to Stripe'
                                    onClick={() => scrollToSection('subscriptions')}
                                />
                                <MetricCard
                                    label='Triggered by Roadmap'
                                    value={String(data.paywallFeedback.thresholdTriggers.milestones + data.paywallFeedback.thresholdTriggers.both)}
                                    subtitle={`${data.paywallFeedback.thresholdTriggers.milestones} roadmap only, ${data.paywallFeedback.thresholdTriggers.both} both`}
                                />
                                <MetricCard
                                    label='Triggered by Chat'
                                    value={String(data.paywallFeedback.thresholdTriggers.chats + data.paywallFeedback.thresholdTriggers.both)}
                                    subtitle={`${data.paywallFeedback.thresholdTriggers.chats} chat only, ${data.paywallFeedback.thresholdTriggers.both} both`}
                                />
                            </div>
                            {data.paywallFeedback.reasonBreakdown.length > 0 && (
                                <div className='px-4 sm:px-6 lg:px-8'>
                                    <div className='bg-white rounded-xl border border-gray-200 p-6 shadow-sm'>
                                        <h3 className='text-sm font-semibold text-gray-700 mb-3'>Why users aren't subscribing</h3>
                                        <div className='space-y-2'>
                                            {data.paywallFeedback.reasonBreakdown.map((r) => {
                                                const total = data.paywallFeedback!.reasonBreakdown.reduce((s, x) => s + x.count, 0)
                                                const pct = total > 0 ? (r.count / total) * 100 : 0
                                                return (
                                                    <div key={r._id} className='flex items-center gap-3'>
                                                        <div className='flex-1'>
                                                            <div className='flex items-center justify-between mb-1'>
                                                                <span className='text-sm text-gray-600'>
                                                                    {FEEDBACK_REASON_LABELS[r._id] || r._id}
                                                                </span>
                                                                <span className='text-sm font-medium text-gray-900'>
                                                                    {r.count} ({pct.toFixed(0)}%)
                                                                </span>
                                                            </div>
                                                            <div className='h-2 bg-gray-100 rounded-full overflow-hidden'>
                                                                <div
                                                                    className='h-full rounded-full bg-cyan-500 transition-all duration-500'
                                                                    style={{ width: `${pct}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ---- Drill-Down Detail Sections ---- */}

                    <div className='px-4 sm:px-6 lg:px-8 pt-6'>
                        <div className='border-t border-gray-200' />
                    </div>

                    {/* Recent Registrations */}
                    {data.recentRegistrations && (
                        <CollapsibleSection
                            id='registrations'
                            title='Recent Registrations'
                            count={data.recentRegistrations.length}
                            isOpen={!!openSections.registrations}
                            onToggle={() => toggleSection('registrations')}
                        >
                            <Table
                                data={data.recentRegistrations}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Registered',
                                        cell: (row: RegistrationRow) => formatDate(row.createdAt),
                                        sort: (a: RegistrationRow, b: RegistrationRow) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                                    },
                                    {
                                        header: 'Onboarded',
                                        cell: (row: RegistrationRow) => <BoolBadge value={row.onboardingComplete} />,
                                        filter: [
                                            { label: 'Yes', fn: (row: RegistrationRow) => !!row.onboardingComplete },
                                            { label: 'No', fn: (row: RegistrationRow) => !row.onboardingComplete },
                                        ],
                                    },
                                    {
                                        header: 'Subscription',
                                        cell: (row: RegistrationRow) => <StatusBadge status={row.subscriptionStatus} />,
                                        filter: [
                                            { label: 'Has Sub', fn: (row: RegistrationRow) => !!row.subscriptionStatus },
                                            { label: 'No Sub', fn: (row: RegistrationRow) => !row.subscriptionStatus },
                                        ],
                                    },
                                    {
                                        header: 'Last Login',
                                        cell: (row: RegistrationRow) => formatAgo(row.lastLoginAt),
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}

                    {/* Onboarding Drop-offs */}
                    {data.onboardingDropoffs && (
                        <CollapsibleSection
                            id='dropoffs'
                            title='Onboarding Drop-offs'
                            count={data.onboardingDropoffs.length}
                            isOpen={!!openSections.dropoffs}
                            onToggle={() => toggleSection('dropoffs')}
                        >
                            <Table
                                data={data.onboardingDropoffs}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Registered',
                                        cell: (row: DropoffRow) => formatDate(row.createdAt),
                                        sort: (a: DropoffRow, b: DropoffRow) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                                    },
                                    {
                                        header: 'Waiting Since',
                                        cell: (row: DropoffRow) => formatAgo(row.createdAt),
                                    },
                                    {
                                        header: 'Last Login',
                                        cell: (row: DropoffRow) => formatAgo(row.lastLoginAt),
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}

                    {/* Engagement Leaderboard */}
                    {data.engagementLeaderboard && (
                        <CollapsibleSection
                            id='engagement'
                            title='Engagement Leaderboard'
                            count={data.engagementLeaderboard.length}
                            isOpen={!!openSections.engagement}
                            onToggle={() => toggleSection('engagement')}
                        >
                            <Table
                                data={data.engagementLeaderboard}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Onboarded',
                                        cell: (row: EngagementRow) => <BoolBadge value={row.onboardingComplete} />,
                                        filter: [
                                            { label: 'Yes', fn: (row: EngagementRow) => row.onboardingComplete },
                                            { label: 'No', fn: (row: EngagementRow) => !row.onboardingComplete },
                                        ],
                                    },
                                    {
                                        header: 'Chats',
                                        cell: (row: EngagementRow) => String(row.chatCount),
                                        sort: (a: EngagementRow, b: EngagementRow) => b.chatCount - a.chatCount,
                                    },
                                    {
                                        header: 'Milestones',
                                        cell: (row: EngagementRow) => `${row.completedMilestones}/${row.totalMilestones}`,
                                        sort: (a: EngagementRow, b: EngagementRow) => b.completedMilestones - a.completedMilestones,
                                    },
                                    {
                                        header: 'Tasks Done',
                                        cell: (row: EngagementRow) => String(row.completedTasks),
                                        sort: (a: EngagementRow, b: EngagementRow) => b.completedTasks - a.completedTasks,
                                    },
                                    {
                                        header: 'Last Login',
                                        cell: (row: EngagementRow) => formatAgo(row.lastLoginAt),
                                        sort: (a: EngagementRow, b: EngagementRow) => new Date(b.lastLoginAt || 0).getTime() - new Date(a.lastLoginAt || 0).getTime(),
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}

                    {/* Churn Detail */}
                    {data.churnDetail && data.churnDetail.length > 0 && (
                        <CollapsibleSection
                            id='churn'
                            title='Churn Detail'
                            count={data.churnDetail.length}
                            isOpen={!!openSections.churn}
                            onToggle={() => toggleSection('churn')}
                        >
                            <Table
                                data={data.churnDetail}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Plan',
                                        cell: (row: ChurnRow) => row.planType || '\u2014',
                                    },
                                    {
                                        header: 'Was Trial',
                                        cell: (row: ChurnRow) => <BoolBadge value={!!row.trialEndDate} />,
                                        filter: [
                                            { label: 'Trial', fn: (row: ChurnRow) => !!row.trialEndDate },
                                            { label: 'Paid', fn: (row: ChurnRow) => !row.trialEndDate },
                                        ],
                                    },
                                    {
                                        header: 'Subscribed',
                                        cell: (row: ChurnRow) => formatDate(row.createdAt),
                                        sort: (a: ChurnRow, b: ChurnRow) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                                    },
                                    {
                                        header: 'Canceled',
                                        cell: (row: ChurnRow) => formatDate(row.updatedAt),
                                        sort: (a: ChurnRow, b: ChurnRow) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                                    },
                                    {
                                        header: 'Days Active',
                                        cell: (row: ChurnRow) => String(row.daysActive),
                                        sort: (a: ChurnRow, b: ChurnRow) => b.daysActive - a.daysActive,
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}

                    {/* Subscriptions */}
                    {data.subscriptions.length > 0 && (
                        <CollapsibleSection
                            id='subscriptions'
                            title='Subscriptions'
                            count={data.subscriptions.length}
                            isOpen={!!openSections.subscriptions}
                            onToggle={() => toggleSection('subscriptions')}
                        >
                            <Table
                                data={data.subscriptions}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Status',
                                        cell: (row: SubscriptionRow) => <StatusBadge status={row.subscriptionStatus} />,
                                        filter: [
                                            { label: 'Active', fn: (row: SubscriptionRow) => row.subscriptionStatus === 'active' },
                                            { label: 'Trialing', fn: (row: SubscriptionRow) => row.subscriptionStatus === 'trialing' },
                                            { label: 'Canceled', fn: (row: SubscriptionRow) => row.subscriptionStatus === 'canceled' },
                                            { label: 'Past Due', fn: (row: SubscriptionRow) => row.subscriptionStatus === 'past_due' },
                                            { label: 'Paused', fn: (row: SubscriptionRow) => row.subscriptionStatus === 'paused' },
                                        ],
                                    },
                                    {
                                        header: 'Plan',
                                        cell: (row: SubscriptionRow) => row.planType || '\u2014',
                                    },
                                    {
                                        header: 'Created',
                                        cell: (row: SubscriptionRow) => formatDate(row.createdAt),
                                        sort: (a: SubscriptionRow, b: SubscriptionRow) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                                    },
                                    {
                                        header: 'Trial Ends',
                                        cell: (row: SubscriptionRow) => formatDate(row.trialEndDate),
                                    },
                                    {
                                        header: 'Benefits End',
                                        cell: (row: SubscriptionRow) => formatDate(row.benefitsEndDate),
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}

                    {/* Recent Logins */}
                    <CollapsibleSection
                        id='logins'
                        title='Recent Logins'
                        count={data.recentLogins.length}
                        isOpen={!!openSections.logins}
                        onToggle={() => toggleSection('logins')}
                    >
                        <Table
                            data={data.recentLogins}
                            columns={[
                                { header: 'Name', cell: 'name' },
                                { header: 'Email', cell: 'email' },
                                {
                                    header: 'Login Time',
                                    cell: (row) => format(new Date(row.timestamp), 'MMM d, yyyy h:mm a'),
                                    sort: (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
                                },
                            ]}
                            hideSearch
                            hidePagination
                        />
                    </CollapsibleSection>

                    {/* Paywall Feedback */}
                    {data.paywallFeedback && data.paywallFeedback.recentFeedback.length > 0 && (
                        <CollapsibleSection
                            id='paywallFeedback'
                            title='Paywall Feedback'
                            count={data.paywallFeedback.recentFeedback.length}
                            isOpen={!!openSections.paywallFeedback}
                            onToggle={() => toggleSection('paywallFeedback')}
                        >
                            <Table
                                data={data.paywallFeedback.recentFeedback}
                                columns={[
                                    { header: 'Name', cell: 'name' },
                                    { header: 'Email', cell: 'email' },
                                    {
                                        header: 'Reason',
                                        cell: (row: PaywallFeedbackRow) => FEEDBACK_REASON_LABELS[row.reason] || row.reason,
                                        filter: [
                                            { label: 'Don\'t want to pay', fn: (row: PaywallFeedbackRow) => row.reason === 'useful_free' },
                                            { label: 'Too expensive', fn: (row: PaywallFeedbackRow) => row.reason === 'too_expensive' },
                                            { label: 'Not ready', fn: (row: PaywallFeedbackRow) => row.reason === 'not_ready' },
                                            { label: 'Other', fn: (row: PaywallFeedbackRow) => row.reason === 'other' },
                                        ],
                                    },
                                    {
                                        header: 'Details',
                                        cell: (row: PaywallFeedbackRow) => row.otherText || '\u2014',
                                    },
                                    {
                                        header: 'Date',
                                        cell: (row: PaywallFeedbackRow) => formatDate(row.createdAt),
                                        sort: (a: PaywallFeedbackRow, b: PaywallFeedbackRow) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                                    },
                                ]}
                                hideSearch
                            />
                        </CollapsibleSection>
                    )}
                </>
            ) : null}
        </div>
    )
}
